import type { CSSProperties, ReactNode } from "react";

import type { ProfileMetricsBarItemInput } from "./profileHeaderFacts";
import {
  ProfileMetricsBarClockIcon,
  ProfileMetricsBarPencilIcon,
  ProfileMetricsBarRepeatIcon,
  ProfileMetricsBarShieldCheckIcon,
  ProfileMetricsBarStarIcon,
} from "./profileMetricsBarIcons";

export type ProfileMetricsBarMetricId = "missionsCompleted" | "flightHours";

export type ProfileMetricsBarProps = Readonly<{
  ariaLabel: string;
  boostLabel?: string;
  className?: string;
  componentInstance?: string;
  emptyValue?: string;
  editReportedMetricLabel?: (metricLabel: string) => string;
  /** Owner-only Boost / Verify / edit controls. Public viewers must leave this false. */
  interactive?: boolean;
  isMock?: boolean;
  items: readonly ProfileMetricsBarItemInput[];
  onBoostRating?: () => void;
  onEditReportedMetric?: (metricId: ProfileMetricsBarMetricId) => void;
  onVerifyMetric?: (metricId: ProfileMetricsBarMetricId) => void;
  verifyLabel?: string;
}>;

function isEmptyMetricValue(value: string | undefined, emptyValue: string): boolean {
  return !value || value === emptyValue;
}

function MetricsActionPill({
  disabled,
  icon,
  label,
  onClick,
}: Readonly<{
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}>) {
  return (
    <button
      type="button"
      className="profile-metrics-bar__action-pill"
      disabled={disabled || !onClick}
      onClick={onClick}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function MetricsActionRow({ children }: Readonly<{ children?: ReactNode }>) {
  return (
    <div className="profile-metrics-bar__action-row" aria-hidden={children ? undefined : true}>
      {children ?? null}
    </div>
  );
}

function MetricsEditButton({
  ariaLabel,
  onClick,
}: Readonly<{
  ariaLabel: string;
  onClick?: () => void;
}>) {
  return (
    <button
      type="button"
      className="profile-metrics-bar__edit-button"
      aria-label={ariaLabel}
      disabled={!onClick}
      onClick={onClick}
    >
      <ProfileMetricsBarPencilIcon className="profile-metrics-bar__edit-icon" />
    </button>
  );
}

function MetricsFooterLabel({ label }: Readonly<{ label: string }>) {
  return <p className="profile-metrics-bar__label">{label}</p>;
}

function ProfileMetricsBarPair({
  emptyValue,
  interactive,
  item,
  editReportedMetricLabel,
  onEditReportedMetric,
  onVerifyMetric,
  verifyLabel,
}: Readonly<{
  emptyValue: string;
  interactive: boolean;
  item: ProfileMetricsBarItemInput;
  editReportedMetricLabel: (metricLabel: string) => string;
  onEditReportedMetric?: (metricId: ProfileMetricsBarMetricId) => void;
  onVerifyMetric?: (metricId: ProfileMetricsBarMetricId) => void;
  verifyLabel: string;
}>) {
  const metricId: ProfileMetricsBarMetricId = item.id === "flightHours" ? "flightHours" : "missionsCompleted";
  const verifiedValue = item.verifiedValue ?? emptyValue;
  const reportedValue = item.reportedValue ?? emptyValue;
  const verifiedEmpty = isEmptyMetricValue(verifiedValue, emptyValue);
  const reportedEmpty = isEmptyMetricValue(reportedValue, emptyValue);
  const pairDigitSpan = Math.max(
    verifiedValue.replace(/\D/g, "").length,
    reportedValue.replace(/\D/g, "").length,
  );
  const verifyIcon = metricId === "flightHours"
    ? <ProfileMetricsBarClockIcon className="profile-metrics-bar__action-icon" />
    : <ProfileMetricsBarShieldCheckIcon className="profile-metrics-bar__action-icon" />;

  return (
    <article
      className="profile-metrics-bar__item"
      data-component-instance={item.componentInstance}
      data-mock={item.isMock ? "true" : undefined}
      aria-label={item.label}
    >
      {interactive ? (
        <MetricsActionRow>
          <MetricsActionPill
            disabled={item.verifyDisabled}
            icon={verifyIcon}
            label={verifyLabel}
            onClick={() => onVerifyMetric?.(metricId)}
          />
        </MetricsActionRow>
      ) : null}
      <div className="profile-metrics-bar__metric-body">
        <div
          className="profile-metrics-bar__pair-stack"
          data-pair-digits={String(pairDigitSpan)}
        >
          <div className="profile-metrics-bar__pair-column">
            <span
              className={`profile-metrics-bar__value profile-metrics-bar__value--verified${
                verifiedEmpty ? " profile-metrics-bar__value--empty" : ""
              }`}
            >
              {verifiedValue}
            </span>
            <span
              className={`profile-metrics-bar__pair-sublabel profile-metrics-bar__pair-sublabel--verified${
                verifiedEmpty ? " profile-metrics-bar__pair-sublabel--empty" : ""
              }`}
            >
              {item.verifiedLabel}
            </span>
          </div>
          <span className="profile-metrics-bar__pair-divider" aria-hidden="true">/</span>
          <div className="profile-metrics-bar__pair-column profile-metrics-bar__pair-column--reported">
            <span className="profile-metrics-bar__reported-value-row">
              <span
                className={`profile-metrics-bar__value profile-metrics-bar__value--reported${
                  reportedEmpty ? " profile-metrics-bar__value--empty" : ""
                }`}
              >
                {reportedValue}
              </span>
              {interactive && item.reportedEditable ? (
                <MetricsEditButton
                  ariaLabel={editReportedMetricLabel(item.label)}
                  onClick={() => onEditReportedMetric?.(metricId)}
                />
              ) : null}
            </span>
            <span
              className={`profile-metrics-bar__pair-sublabel profile-metrics-bar__pair-sublabel--reported`}
            >
              {item.reportedLabel}
            </span>
          </div>
        </div>
        <MetricsFooterLabel label={item.label} />
      </div>
    </article>
  );
}

function ProfileMetricsBarScore({
  boostLabel,
  emptyValue,
  interactive,
  item,
  onBoostRating,
}: Readonly<{
  boostLabel: string;
  emptyValue: string;
  interactive: boolean;
  item: ProfileMetricsBarItemInput;
  onBoostRating?: () => void;
}>) {
  const value = item.value ?? emptyValue;
  const ratingEmpty = isEmptyMetricValue(value, emptyValue);
  const ratingNumber = Number.parseFloat(value);
  const filledStars = !ratingEmpty && Number.isFinite(ratingNumber)
    ? Math.max(0, Math.min(5, Math.floor(ratingNumber)))
    : 0;

  return (
    <article
      className="profile-metrics-bar__item"
      data-component-instance={item.componentInstance}
      data-mock={item.isMock ? "true" : undefined}
      aria-label={item.label}
    >
      {interactive ? (
        <MetricsActionRow>
          <MetricsActionPill
            icon={<ProfileMetricsBarStarIcon className="profile-metrics-bar__action-icon" variant="outline" />}
            label={boostLabel}
            onClick={onBoostRating}
          />
        </MetricsActionRow>
      ) : null}
      <div className="profile-metrics-bar__metric-body profile-metrics-bar__metric-body--score">
        <div className="profile-metrics-bar__score-values">
          <span
            className={`profile-metrics-bar__value${
              ratingEmpty ? " profile-metrics-bar__value--empty" : ""
            }`}
          >
            {value}
          </span>
          <span className="profile-metrics-bar__muted">/ {item.maxValue}</span>
        </div>
        <div className="profile-metrics-bar__star-row" aria-hidden="true">
          {Array.from({ length: 5 }, (_, index) => (
            <ProfileMetricsBarStarIcon
              key={index}
              className="profile-metrics-bar__star"
              variant={index < filledStars ? "filled" : "outline"}
            />
          ))}
        </div>
        <MetricsFooterLabel label={item.label} />
      </div>
    </article>
  );
}

function ProfileMetricsBarSingle({
  emptyValue,
  interactive,
  item,
}: Readonly<{
  emptyValue: string;
  interactive: boolean;
  item: ProfileMetricsBarItemInput;
}>) {
  const value = item.value ?? emptyValue;
  const singleEmpty = isEmptyMetricValue(value, emptyValue);

  return (
    <article
      className="profile-metrics-bar__item"
      data-component-instance={item.componentInstance}
      data-mock={item.isMock ? "true" : undefined}
      aria-label={item.label}
    >
      {interactive ? <MetricsActionRow /> : null}
      <div className="profile-metrics-bar__metric-body">
        <div className="profile-metrics-bar__value-row profile-metrics-bar__value-row--single">
          <span className="profile-metrics-bar__icon profile-metrics-bar__icon--info" aria-hidden="true">
            <ProfileMetricsBarRepeatIcon className="profile-metrics-bar__svg" />
          </span>
          <span
            className={`profile-metrics-bar__value${
              singleEmpty ? " profile-metrics-bar__value--empty" : ""
            }`}
          >
            {value}
            {item.suffix ? <span className="profile-metrics-bar__suffix">{item.suffix}</span> : null}
          </span>
        </div>
        <MetricsFooterLabel label={item.label} />
      </div>
    </article>
  );
}

export function ProfileMetricsBar({
  ariaLabel,
  boostLabel = "Boost",
  className,
  componentInstance = "profileMetricsBar",
  emptyValue = "--",
  editReportedMetricLabel = (metricLabel) => `Edit ${metricLabel}`,
  interactive = false,
  isMock = false,
  items,
  onBoostRating,
  onEditReportedMetric,
  onVerifyMetric,
  verifyLabel = "Verify",
}: ProfileMetricsBarProps) {
  if (items.length === 0) {
    return null;
  }

  const style = { "--profile-metrics-count": items.length } as CSSProperties;
  const rootClassName = [
    "profile-metrics-bar",
    interactive ? "profile-metrics-bar--interactive" : "",
    className ?? "",
  ].filter(Boolean).join(" ");
  const documentLang = typeof document !== "undefined"
    ? document.documentElement.lang || undefined
    : undefined;

  return (
    <section
      className={rootClassName}
      data-component-instance={componentInstance}
      data-mock={isMock ? "true" : undefined}
      aria-label={ariaLabel}
      lang={documentLang}
      style={style}
    >
      {items.map((item) => {
        if (item.kind === "pair") {
          return (
            <ProfileMetricsBarPair
              editReportedMetricLabel={editReportedMetricLabel}
              key={item.id}
              emptyValue={emptyValue}
              interactive={interactive}
              item={item}
              onEditReportedMetric={onEditReportedMetric}
              onVerifyMetric={onVerifyMetric}
              verifyLabel={verifyLabel}
            />
          );
        }

        if (item.kind === "score") {
          return (
            <ProfileMetricsBarScore
              key={item.id}
              boostLabel={boostLabel}
              emptyValue={emptyValue}
              interactive={interactive}
              item={item}
              onBoostRating={onBoostRating}
            />
          );
        }

        return (
          <ProfileMetricsBarSingle
            key={item.id}
            emptyValue={emptyValue}
            interactive={interactive}
            item={item}
          />
        );
      })}
    </section>
  );
}
